import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, LogOut, Inbox, Users, Settings2, Loader2, Power } from "lucide-react";
import { PageShell, Eyebrow } from "../components/Layout";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("rp_admin_token") || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState(null);
  const [subscribers, setSubscribers] = useState(null);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const logout = useCallback(() => {
    localStorage.removeItem("rp_admin_token");
    setToken("");
    setEnquiries(null); setSubscribers(null); setSettings(null);
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [e, s, st] = await Promise.all([
        axios.get(`${API}/admin/enquiries`, { headers: authHeaders() }),
        axios.get(`${API}/admin/subscribers`, { headers: authHeaders() }),
        axios.get(`${API}/admin/settings`, { headers: authHeaders() }),
      ]);
      setEnquiries(e.data); setSubscribers(s.data); setSettings(st.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast({ title: "Session expired", description: "Please sign in again." });
        logout();
      }
    }
  }, [authHeaders, logout, toast]);

  useEffect(() => { if (token) loadAll(); }, [token, loadAll]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("rp_admin_token", r.data.token);
      setToken(r.data.token);
      setPassword("");
      toast({ title: "Welcome back", description: "Admin session started." });
    } catch (err) {
      toast({ title: "Login failed", description: err?.response?.data?.detail || "Incorrect password." });
    } finally {
      setLoggingIn(false);
    }
  };

  const updateSettings = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/settings`, patch, { headers: authHeaders() });
      setSettings((s) => ({ ...s, ...r.data }));
      toast({ title: "Settings saved", description: patch.ai_enabled !== undefined ? `AI Advisor ${r.data.ai_enabled ? "enabled" : "disabled"}.` : `Model set to ${r.data.ai_model}.` });
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast({ title: "Save failed", description: err?.response?.data?.detail || "Try again." });
    } finally {
      setSaving(false);
    }
  };

  if (!token) {
    return (
      <PageShell>
        <section className="hero-grid min-h-[70vh] flex items-center">
          <div className="max-w-md mx-auto w-full px-5">
            <div className="border border-neutral-200 rounded-lg p-9 bg-white shadow-[0_20px_60px_rgba(15,27,45,0.08)]">
              <div className="w-11 h-11 rounded-md bg-ink text-white flex items-center justify-center mb-5"><Lock size={18} /></div>
              <h1 className="font-heading text-2xl font-bold text-ink">Admin console</h1>
              <p className="text-[13.5px] text-ink/55 mt-1.5">Enquiries, subscribers and AI Advisor controls.</p>
              <form onSubmit={login} className="mt-7 space-y-4">
                <div>
                  <label className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-neutral-500 block mb-2">Password</label>
                  <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input"
                    className="w-full border border-neutral-300 rounded-md px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-accent" placeholder="••••••••" />
                </div>
                <button type="submit" disabled={loggingIn} data-testid="admin-login-btn"
                  className="w-full bg-accent text-white rounded-md py-3 text-sm font-semibold hover:bg-accent-dark transition-colors duration-200 disabled:opacity-60">
                  {loggingIn ? "Signing in…" : "Sign in"}
                </button>
              </form>
            </div>
          </div>
        </section>
      </PageShell>
    );
  }

  const TABS = [
    { key: "enquiries", label: "Enquiries", icon: Inbox, count: enquiries?.length },
    { key: "subscribers", label: "Subscribers", icon: Users, count: subscribers?.length },
    { key: "settings", label: "AI Settings", icon: Settings2 },
  ];

  return (
    <PageShell>
      <section className="bg-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 hero-grid-dark pointer-events-none" />
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 pt-12 pb-10 relative flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow light>Admin console</Eyebrow>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight">Operations desk.</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2.5 rounded-md text-[13px] font-semibold hover:border-white transition-colors duration-200">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-8">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-[13.5px] font-semibold border transition-colors duration-150 ${tab === t.key ? "bg-ink text-white border-ink" : "border-neutral-300 text-ink/60 hover:border-ink"}`}>
                <t.icon size={15} /> {t.label}
                {t.count !== undefined && t.count !== null && <span className={`font-mono text-[10.5px] px-1.5 py-0.5 rounded-sm ${tab === t.key ? "bg-white/15" : "bg-neutral-100"}`}>{t.count}</span>}
              </button>
            ))}
          </div>

          {tab === "enquiries" && (
            <div className="border border-neutral-200 rounded-lg overflow-hidden" data-testid="admin-enquiries-panel">
              {!enquiries ? (
                <div className="p-10 flex items-center gap-2 text-neutral-400 text-sm"><Loader2 size={15} className="animate-spin" /> Loading…</div>
              ) : enquiries.length === 0 ? (
                <div className="p-10 text-center text-ink/50 text-sm">No enquiries yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-paper border-b border-neutral-200">
                        {["Received", "Name", "Email", "Company", "Topic", "Message"].map((h) => (
                          <th key={h} className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-400 px-5 py-3.5 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {enquiries.map((e) => (
                        <tr key={e.id} className="border-b border-neutral-100 last:border-0 hover:bg-paper/60 transition-colors duration-100 align-top">
                          <td className="px-5 py-4 font-mono text-[11px] text-neutral-400 whitespace-nowrap">{fmt(e.created_at)}</td>
                          <td className="px-5 py-4 text-[13.5px] font-medium text-ink whitespace-nowrap">{e.name}</td>
                          <td className="px-5 py-4 text-[13px] text-ink/70 whitespace-nowrap">{e.email}</td>
                          <td className="px-5 py-4 text-[13px] text-ink/60 whitespace-nowrap">{e.company || "—"}</td>
                          <td className="px-5 py-4"><span className="font-mono text-[10.5px] border border-neutral-300 text-ink/60 px-2 py-0.5 rounded-sm whitespace-nowrap">{e.topic}</span></td>
                          <td className="px-5 py-4 text-[13px] text-ink/70 max-w-md">{e.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "subscribers" && (
            <div className="border border-neutral-200 rounded-lg overflow-hidden" data-testid="admin-subscribers-panel">
              {!subscribers ? (
                <div className="p-10 flex items-center gap-2 text-neutral-400 text-sm"><Loader2 size={15} className="animate-spin" /> Loading…</div>
              ) : subscribers.length === 0 ? (
                <div className="p-10 text-center text-ink/50 text-sm">No subscribers yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-paper border-b border-neutral-200">
                        {["Subscribed", "Email", "Role"].map((h) => (
                          <th key={h} className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-400 px-5 py-3.5">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((s) => (
                        <tr key={s.id} className="border-b border-neutral-100 last:border-0 hover:bg-paper/60 transition-colors duration-100">
                          <td className="px-5 py-4 font-mono text-[11px] text-neutral-400 whitespace-nowrap">{fmt(s.subscribed_at)}</td>
                          <td className="px-5 py-4 text-[13.5px] font-medium text-ink">{s.email}</td>
                          <td className="px-5 py-4 text-[13px] text-ink/60">{s.role || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "settings" && settings && (
            <div className="max-w-2xl space-y-5" data-testid="admin-settings-panel">
              <div className="border border-neutral-200 rounded-lg p-7 flex items-start justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2.5">
                    <Power size={17} className={settings.ai_enabled ? "text-emerald-500" : "text-neutral-400"} />
                    <h2 className="font-heading text-lg font-bold text-ink">AI Advisor</h2>
                  </div>
                  <p className="text-[13px] text-ink/55 mt-2 leading-relaxed">When disabled, the public advisor panel shows an offline notice and the chat API returns 503.</p>
                </div>
                <button onClick={() => updateSettings({ ai_enabled: !settings.ai_enabled })} disabled={saving} data-testid="admin-ai-toggle"
                  role="switch" aria-checked={settings.ai_enabled}
                  className={`relative w-14 h-8 rounded-full shrink-0 transition-colors duration-200 disabled:opacity-50 ${settings.ai_enabled ? "bg-emerald-500" : "bg-neutral-300"}`}>
                  <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-[left] duration-200 ${settings.ai_enabled ? "left-7" : "left-1"}`} />
                </button>
              </div>

              <div className="border border-neutral-200 rounded-lg p-7">
                <h2 className="font-heading text-lg font-bold text-ink">Model</h2>
                <p className="text-[13px] text-ink/55 mt-1.5">Applies to all four personas immediately.</p>
                <div className="mt-5 space-y-2.5">
                  {(settings.available_models || []).map((m) => (
                    <button key={m.key} onClick={() => updateSettings({ ai_model: m.key })} disabled={saving} data-testid={`admin-model-${m.key}`}
                      className={`w-full flex items-center justify-between border rounded-md px-4 py-3.5 text-left transition-colors duration-150 disabled:opacity-60 ${settings.ai_model === m.key ? "border-accent bg-accent/[0.04]" : "border-neutral-200 hover:border-neutral-400"}`}>
                      <span className="text-[14px] font-medium text-ink">{m.label}</span>
                      <span className={`w-4 h-4 rounded-full border-2 ${settings.ai_model === m.key ? "border-accent bg-accent" : "border-neutral-300"}`} />
                    </button>
                  ))}
                </div>
                {saving && <p className="flex items-center gap-2 text-neutral-400 text-[12px] mt-4"><Loader2 size={13} className="animate-spin" /> Saving…</p>}
              </div>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
};

export default Admin;
